import { bisector } from 'd3-array'
import { ScaleLinear } from 'd3-scale'

import { throttle } from 'lodash'

import CanvasChart from '../common/CanvasChart'
import Legend, { LegendItem } from '../common/Legend'
import { RigstateItem, Span, Start } from 'webapp/js/types'
type State = {
    start: Start
    items: RigstateItem[]
    span?: Span
}

export default class RigStateChart extends CanvasChart<State> {
    protected legend_: Legend
    protected legendRenderHoveredValues: (x: { text: string }[]) => void

    init(): void {
        super.init()
        this.el.classed('rigstate-chart', true)

        this.legend_ = new Legend(this._legend.node())

        this.legendRenderHoveredValues = throttle((x: { text: string }[]) => this.legend_.renderHoveredValues(x), 100)
    }

    onMouseMove(mouse: [number, number], scale: ScaleLinear<number, number>): void {
        const data = this.subject.getValue()
        if (data.items?.length > 0) {
            const bisectorValue = bisector((x: { timestamp: number }) => x.timestamp).left
            const x = mouse[this.isVertical ? 1 : 0]
            const value = scale.invert(x)

            const index = bisectorValue(data.items, value)
            if (index !== undefined) {
                const section = data.items[index - 1]
                if (section) {
                    const contents = this._contents.node()
                    if (contents) {
                        this.legendRenderHoveredValues([
                            {
                                text: section.rig_state
                            }
                        ])
                    }
                }
            }
        }
    }

    buildLegend(data: State): LegendItem[] {
        const legend = data.items.reduce((state, item) => {
            if (state[item.rig_state]) {
                return state
            }

            return {
                ...state,
                [item.rig_state]: item.__color
            }
        }, {})

        return Object.keys(legend).map(name => ({ text: name, color: legend[name] }))
    }

    renderLegend(data: State): void {
        this._legend
            .style('height', this.isVertical ? `${this.legendSize}px` : 'auto')
            .style('width', this.isVertical ? 'auto' : `${this.legendSize}px`)
        if (data.items?.length > 0) {
            this.legend_.render(this.buildLegend(data), this.isVertical ?? false, this.legendSize)
        }
    }

    renderCanvas(canvas: HTMLCanvasElement, data: State, scale: ScaleLinear<number, number>): void {
        const ctx: CanvasRenderingContext2D | null = canvas?.getContext('2d')
        const { width, height } = canvas

        if (ctx) {
            ctx.clearRect(0, 0, width, height)
            if (data.items?.length > 0) {
                ctx.beginPath()
                for (let i = 1; i < data.items.length; i++) {
                    const { timestamp } = data.items[i]
                    const prev = data.items[i - 1]
                    const y1 = Math.round(scale(prev.timestamp))
                    const y2 = Math.round(scale(timestamp))
                    const length = y2 - y1
                    if (length === 0) {
                        continue
                    }
                    ctx.fillStyle = prev.__color
                    this.isVertical ? ctx.fillRect(0, y1, width, length) : ctx.fillRect(y1, 0, length, height)
                }

                // print last/current
                const last = data.items[data.items.length - 1]
                const y1 = scale(last.timestamp)
                const y2 = scale(new Date().getTime())
                ctx.fillStyle = last.__color
                this.isVertical ? ctx.fillRect(0, y1, width, y2 - y1) : ctx.fillRect(y1, 0, y2 - y1, height)

                ctx.fill()
            }
        }
    }
}
